import { z } from 'zod';
import { Id, Limit, Seq, Timestamp } from './common';
import { NoContent, endpoint } from './endpoint';
import { lenient } from './lenient';
import { FileInfo } from './files';
import { LIMITS } from './limits';
import { LinkPreviewList } from './links';

export const HistoryMode = z.enum(['after_join', 'include_history']);
export type HistoryMode = z.infer<typeof HistoryMode>;

/**
 * Structured system lines rendered by the client (design "sysmsg"). A line
 * type added by a newer server reads as `unsupported` (shown as a generic line).
 */
export const SystemEvent = lenient(
  z.discriminatedUnion('type', [
  z.object({ type: z.literal('direct.started'), actorId: Id }),
  z.object({ type: z.literal('group.created'), actorId: Id, fromDirect: z.boolean() }),
  z.object({ type: z.literal('member.added'), actorId: Id, userIds: z.array(Id).min(1), historyMode: HistoryMode }),
  z.object({ type: z.literal('member.left'), userId: Id }),
  z.object({ type: z.literal('member.removed'), actorId: Id, userId: Id }),
  z.object({ type: z.literal('history_mode.changed'), actorId: Id, historyMode: HistoryMode }),
  z.object({ type: z.literal('title.changed'), actorId: Id, title: z.string() }),
  z.object({ type: z.literal('owner.changed'), fromId: Id.nullable(), toId: Id }),
  z.object({ type: z.literal('account.deactivated'), userId: Id }),
  z.object({ type: z.literal('unsupported') }),
  ]),
  { type: 'unsupported' },
);
export type SystemEvent = z.infer<typeof SystemEvent>;

/** Quote shown above a reply. Never a stored copy of the original text. */
export const ReplyRef = z.object({
  messageId: Id,
  senderId: Id.nullable(),
  /** Current excerpt (≤ 120 chars) or null when deleted / outside the viewer's history range. */
  excerpt: z.string().nullable(),
  /** First attachment name when the original has no text. */
  attachmentName: z.string().nullable(),
  state: lenient(z.enum(['available', 'deleted', 'unavailable']), 'unavailable'),
});
export type ReplyRef = z.infer<typeof ReplyRef>;

/** Read receipt for the viewer's own messages: how many eligible members have read it. */
export const Receipt = z.object({
  readCount: z.number().int().nonnegative(),
  /** Other active members whose visible range includes this message. */
  eligibleCount: z.number().int().nonnegative(),
});
export type Receipt = z.infer<typeof Receipt>;

let rgiEmoji: RegExp | null | undefined;

/**
 * One emoji of Unicode's recommended (RGI) set, written the recommended way: "❤️", not "❤".
 * The `v` flag is compiled on first use, so an older browser can still load this module;
 * there any text passes, and the server, which always has the flag, decides.
 */
export function isReactionEmoji(value: string): boolean {
  if (rgiEmoji === undefined) {
    try {
      rgiEmoji = new RegExp('^\\p{RGI_Emoji}$', 'v');
    } catch {
      rgiEmoji = null;
    }
  }
  return rgiEmoji ? rgiEmoji.test(value) : value.length > 0;
}

/** The emoji of addReaction / removeReaction. The longest RGI sequences are 15 UTF-16 units. */
export const ReactionEmoji = z.string().min(1).max(32).refine(isReactionEmoji, 'must be a single emoji');

/** One emoji under a message, as the viewer sees it. */
export const Reaction = z.object({
  /** Shown as it is: a newer server may allow reactions this app's picker does not offer. */
  emoji: z.string(),
  count: z.number().int().nonnegative(),
  /** The viewer is among the people who reacted. */
  me: z.boolean(),
  /** The first people who reacted, the viewer first (≤ LIMITS.reactionPreviewUsers); everyone via messages.reactions. */
  userIds: z.array(Id),
});
export type Reaction = z.infer<typeof Reaction>;

/** A message's reactions after a change: the answer to addReaction / removeReaction and the `reaction.updated` event. */
export const MessageReactions = z.object({
  messageId: Id,
  reactions: z.array(Reaction),
  /** See `Message.reactionEventSeq`; "0" when nobody ever reacted. */
  reactionEventSeq: Seq,
});
export type MessageReactions = z.infer<typeof MessageReactions>;

export const Message = z.object({
  id: Id,
  conversationId: Id,
  /** Per-conversation order. */
  seq: Seq,
  /** Null for system lines. */
  senderId: Id.nullable(),
  /** Sender's idempotency key; present only on the viewer's own messages. */
  clientMessageId: Id.nullable(),
  /** `unsupported`: a message kind added by a newer server. */
  kind: lenient(z.enum(['text', 'system', 'unsupported']), 'unsupported'),
  /** Null when deleted (tombstone) or for system lines. */
  text: z.string().nullable(),
  system: SystemEvent.nullable(),
  replyTo: ReplyRef.nullable(),
  attachments: z.array(FileInfo),
  mentions: z.array(Id),
  createdAt: Timestamp,
  editedAt: Timestamp.nullable(),
  revision: z.number().int().nonnegative(),
  deletedAt: Timestamp.nullable(),
  /** Only on the viewer's own text messages. */
  receipt: Receipt.nullable(),
  /**
   * `reactions.v1`, grouped by emoji in the order each was first used. Absent from older
   * servers and on messages nobody ever reacted to; empty once a message is deleted.
   */
  reactions: z.array(Reaction).optional(),
  /** Conversation eventSeq of the latest reaction change: of two copies, the higher one has the newer reactions. */
  reactionEventSeq: Seq.optional(),
  /** Cards for links in `text`, added by the server after the message was stored (`message.link_previews`). */
  linkPreviews: LinkPreviewList.optional(),
  /** Sent with "전달" from another conversation (`messages.forward`); where from is not told. Absent from older servers and when not. */
  forwarded: z.boolean().optional(),
});
export type Message = z.infer<typeof Message>;

/** Conversation-list preview of the latest visible message. */
export const MessagePreview = z.object({
  messageId: Id,
  senderId: Id.nullable(),
  kind: lenient(z.enum(['text', 'attachment', 'system', 'deleted', 'unsupported']), 'unsupported'),
  /** ≤ 140 chars of text, or null. */
  text: z.string().nullable(),
  system: SystemEvent.nullable(),
  attachmentName: z.string().nullable(),
  attachmentCount: z.number().int().nonnegative(),
  createdAt: Timestamp,
});
export type MessagePreview = z.infer<typeof MessagePreview>;

const conversationParam = z.object({ conversationId: Id });
const messageParam = z.object({ conversationId: Id, messageId: Id });
const reactionParam = z.object({ conversationId: Id, messageId: Id, emoji: ReactionEmoji });

/** "예약 전송" (`messages.scheduled`): text the company server sends as its writer at `sendAt`. */
export const ScheduledMessage = z.object({
  id: Id,
  conversationId: Id,
  text: z.string(),
  mentions: z.array(Id),
  sendAt: Timestamp,
  createdAt: Timestamp,
  /** `failed`: not sent, see `error` (left the conversation, read-only, …). */
  status: lenient(z.enum(['scheduled', 'sent', 'failed', 'canceled']), 'scheduled'),
  sentMessageId: Id.nullable(),
  error: z.string().nullable(),
});
export type ScheduledMessage = z.infer<typeof ScheduledMessage>;

export const SendMessageBody = z
  .object({
    /** Generated before the first attempt and reused on every retry. */
    clientMessageId: Id,
    text: z.string().max(LIMITS.maxMessageChars),
    attachmentIds: z.array(Id).max(LIMITS.maxFilesPerMessage).default([]),
    replyToMessageId: Id.optional(),
    mentions: z.array(Id).max(100).default([]),
  })
  .refine((v) => v.text.trim().length > 0 || v.attachmentIds.length > 0, 'message needs text or attachments');
export type SendMessageBody = z.input<typeof SendMessageBody>;

export const messagesEndpoints = {
  list: endpoint({
    id: 'messages.list',
    method: 'GET',
    path: '/api/v1/conversations/:conversationId/messages',
    auth: 'session',
    params: conversationParam,
    query: z.object({
      /** Page older than this seq. */
      before: Seq.optional(),
      /** Page newer than this seq. */
      after: Seq.optional(),
      /** Page centred on a message (search → original position). */
      around: Id.optional(),
      limit: Limit(100, 50),
    }),
    response: z.object({
      messages: z.array(Message),
      hasMoreBefore: z.boolean(),
      hasMoreAfter: z.boolean(),
      /** First seq the viewer may see (group history policy). */
      historyStartSeq: Seq,
    }),
  }),
  /** Idempotent on (sender, clientMessageId): 201 on create, 200 + duplicate=true on replay, 409 IDEMPOTENCY_MISMATCH if content differs. */
  send: endpoint({
    id: 'messages.send',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/messages',
    auth: 'session',
    params: conversationParam,
    body: SendMessageBody,
    response: z.object({ message: Message, duplicate: z.boolean() }),
    successStatus: 201,
  }),
  /**
   * `messages.forward`: the message's text and files, sent by the caller into another conversation they can
   * write in, marked forwarded. Mentions and the reply stay behind (they belong to the first conversation).
   * Idempotent on `clientMessageId` like `send`.
   */
  forward: endpoint({
    id: 'messages.forward',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/forward',
    auth: 'session',
    params: messageParam,
    body: z.object({ clientMessageId: Id, toConversationId: Id }),
    response: z.object({ message: Message, duplicate: z.boolean() }),
    successStatus: 201,
  }),
  /** Text to send later (at least a minute ahead, at most LIMITS.scheduleAheadDays); attachments are not scheduled. */
  schedule: endpoint({
    id: 'messages.schedule',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/scheduled',
    auth: 'session',
    params: conversationParam,
    body: z.object({
      text: z.string().trim().min(1).max(LIMITS.maxMessageChars),
      mentions: z.array(Id).max(100).default([]),
      sendAt: Timestamp,
    }),
    response: z.object({ scheduled: ScheduledMessage }),
    successStatus: 201,
  }),
  /** The caller's messages still waiting to be sent, soonest first, in every conversation. */
  scheduled: endpoint({
    id: 'messages.scheduled',
    method: 'GET',
    path: '/api/v1/me/scheduled',
    auth: 'session',
    response: z.object({ scheduled: z.array(ScheduledMessage).max(LIMITS.maxScheduledMessages) }),
  }),
  /** Not sent after all (only while it still waits). */
  cancelScheduled: endpoint({
    id: 'messages.cancelScheduled',
    method: 'DELETE',
    path: '/api/v1/scheduled/:scheduledId',
    auth: 'session',
    params: z.object({ scheduledId: Id }),
    response: z.object({ scheduled: ScheduledMessage }),
  }),
  /** `messages.saved`: keeps the message in the caller's "저장한 메시지" (idempotent). At most LIMITS.maxSavedMessages, then CONFLICT. */
  save: endpoint({
    id: 'messages.save',
    method: 'PUT',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/saved',
    auth: 'session',
    params: messageParam,
    response: NoContent,
  }),
  /** The ids of every message the caller saved, for the menu and the mark beside a message. */
  savedIds: endpoint({
    id: 'messages.savedIds',
    method: 'GET',
    path: '/api/v1/me/saved/ids',
    auth: 'session',
    response: z.object({ messageIds: z.array(Id).max(LIMITS.maxSavedMessages) }),
  }),
  /**
   * "저장한 메시지", newest saved first, only those the caller can still see (a conversation left or a
   * message deleted since then is not shown, and stays saved until taken out).
   */
  saved: endpoint({
    id: 'messages.saved',
    method: 'GET',
    path: '/api/v1/me/saved',
    auth: 'session',
    query: z.object({ before: Timestamp.optional(), limit: Limit(100, 50) }),
    response: z.object({
      items: z.array(z.object({ savedAt: Timestamp, message: Message })),
      /** Pass as `before` for the next page; null on the last one. */
      nextBefore: Timestamp.nullable(),
    }),
  }),
  /** Takes it out again (idempotent, also for a message the caller can no longer see). */
  unsave: endpoint({
    id: 'messages.unsave',
    method: 'DELETE',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/saved',
    auth: 'session',
    params: messageParam,
    response: NoContent,
  }),
  /** Own messages within the company edit window; optimistic on `baseRevision`. */
  edit: endpoint({
    id: 'messages.edit',
    method: 'PATCH',
    path: '/api/v1/conversations/:conversationId/messages/:messageId',
    auth: 'session',
    params: messageParam,
    body: z.object({ text: z.string().trim().min(1).max(LIMITS.maxMessageChars), baseRevision: z.number().int().nonnegative() }),
    response: z.object({ message: Message }),
  }),
  /** Soft delete: hidden immediately, placeholder kept, content purged after the grace period. */
  delete: endpoint({
    id: 'messages.delete',
    method: 'DELETE',
    path: '/api/v1/conversations/:conversationId/messages/:messageId',
    auth: 'session',
    params: messageParam,
    response: z.object({ message: Message }),
  }),
  readers: endpoint({
    id: 'messages.readers',
    method: 'GET',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/readers',
    auth: 'session',
    params: messageParam,
    response: z.object({
      read: z.array(z.object({ userId: Id, readAt: Timestamp.nullable() })),
      unread: z.array(Id),
    }),
  }),
  /** Everyone who reacted, per emoji, in the order of `Message.reactions` (the summary names only the first few). */
  reactions: endpoint({
    id: 'messages.reactions',
    method: 'GET',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/reactions',
    auth: 'session',
    params: messageParam,
    response: z.object({
      reactions: z.array(z.object({ emoji: z.string(), users: z.array(z.object({ userId: Id, reactedAt: Timestamp })) })),
    }),
  }),
  /** Idempotent: afterwards the viewer has reacted with `emoji`, whether or not they had before. */
  addReaction: endpoint({
    id: 'messages.addReaction',
    method: 'PUT',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/reactions/:emoji',
    auth: 'session',
    params: reactionParam,
    response: MessageReactions,
  }),
  /** Idempotent: afterwards the viewer has not reacted with `emoji`. */
  removeReaction: endpoint({
    id: 'messages.removeReaction',
    method: 'DELETE',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/reactions/:emoji',
    auth: 'session',
    params: reactionParam,
    response: MessageReactions,
  }),
};
